import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';

import { doc, onSnapshot } from 'firebase/firestore';
import db from '@services/firebase-service';
import { logToCloudWatch } from '@services/logger.service';
import { stopEngagement } from '@services/youtube-platform.service';
import { useStreamData } from '@lib/hooks/useStreamData';

import MultipleChoiceOptions from './MultipleChoice/MultipleChoiceOptions';
import { handleEngagement } from '@services/interactions-service';
import { getBarLayout } from '@services/utils.service';
import { isEqual, sortBy } from 'lodash';

const MultipleChoiceAnalytics = ({ analyticsData }) => {
  const relativeMax = analyticsData?.data ? Math.max(...analyticsData?.data?.map((option) => option.contribution ? option.contribution : 0)) : 100;
  return (
    <div className='w-full h-full'>
      <div className='override-font-18 flex flex-col h-full justify-center'>
        {analyticsData?.data?.map((option, index) => (
          <MultipleChoiceOptions
            key={option.key || option.id || `option-${index}`}
            isLiveResponsesVisible={false}
            option={option}
            settings={analyticsData.settings}
            index={index}
            relativeMax={relativeMax}
            showAnswer={true}
            totalOptionCount={analyticsData?.data.length}
            colors={analyticsData.color}
          />
        ))}
      </div>
    </div>
  );
};

const MultipleChoice = forwardRef(({
  streamId,
  platformType,
  currentRosFeature,
  currentRosResult,
  interactionType,
  setCurrentFeatureId,
  startInteraction,
  setShowRosModel,
  moderationModeRef,
  activeInteractionRef,
  updateFSMeta,
  setShowNotification,
  showAnswerMap = {},
  isAnalytics,
  analyticsData,
  setStartTime,
  setTotalResponders
}, ref) => {
  if (isAnalytics) return <MultipleChoiceAnalytics analyticsData={analyticsData} />;

  const {
    firestoreUnsub,
    setFirestoreUnsub,
    setFeatureId
  } = useStreamData(s => ({
    firestoreUnsub: s.firestoreUnsub,
    setFirestoreUnsub: s.setFirestoreUnsub,
    setFeatureId: s.setFeatureId
  }));

  const { isResult, isRos } = interactionType;
  const [multipleChoiceId, setMultipleChoiceId] = useState('');
  const sessionId = localStorage.getItem('sessionId');
  const [interactionResponse, setInteractionResponse] = useState(currentRosFeature.options);
  const [currentSettings, setCurrentSettings] = useState(currentRosFeature.setting);
  const [currentColors, setCurrentColors] = useState(currentRosFeature.color);
  const interactionResponseRef = useRef(interactionResponse);

  const stopMultipleChoice = async () => {
    if (multipleChoiceId) {
      await stopEngagement(streamId, 'multipleChoice', { engagementId: multipleChoiceId, platformType, sessionId });
      setMultipleChoiceId('');
      setFeatureId('');
      if (firestoreUnsub.unSub) {
        firestoreUnsub.unSub();
      }
    }
  };

  const setupMultipleChoice = ({
    id, current, noOfResponders
  }) => {
    setInteractionResponse(current);
    setCurrentFeatureId(id);
    setMultipleChoiceId(id);
    setFeatureId(id);
    setTotalResponders(noOfResponders);
  };

  const handleMultipleChoice = async ({ interactionId = null, interactionResult = [] }) => {
    const response = await handleEngagement({ streamId, platformType, currentRosFeature, interactionId });
    if (response.status && response.entity) {
      const interactionDetails = {
        id: response.entity.engagementId,
        current: interactionResult,
        type: currentRosFeature.type,
        noOfResponders: response.entity.noOfResponders || 0
      };
      setupMultipleChoice(interactionDetails);
      setCurrentSettings(currentRosFeature.setting || currentRosFeature.settings);
      updateFSMeta({ activeInteraction: interactionDetails });
    }
  };

  const handleStartInteraction = () => {
    if (currentRosResult && currentRosResult.length > 0) {
      handleMultipleChoice({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult[0].options });
    } else {
      handleMultipleChoice({ interactionId: null, interactionResult: currentRosFeature.options });
    }
    setStartTime(Date.now());
  };

  const handleStopInteraction = () => {
    stopMultipleChoice();
    setMultipleChoiceId(null);
    if (currentRosResult && currentRosResult[0]?.options) {
      setInteractionResponse(currentRosResult[0]?.options);
    }
  };

  const manageMultipleChoice = () => {
    if (!isResult && startInteraction) {
      handleStartInteraction();
    } else if (multipleChoiceId) {
      handleStopInteraction();
    } else if (currentRosResult && currentRosResult.length > 0) {
      setInteractionResponse(currentRosResult[0].options);
      setTotalResponders(currentRosResult[0].noOfResponders || 0);
    }
  };

  useEffect(() => {
    if (!currentRosFeature.id) return;

    setShowRosModel(false);
    manageMultipleChoice();
  }, [currentRosFeature.id, startInteraction]);

  const unsubscribeFromFirestore = () => {
    if (firestoreUnsub.unsub) {
      firestoreUnsub.unsub();
    }
  };
  useEffect(() => {
    if (currentRosFeature.setting) {
      setCurrentSettings(currentRosFeature.setting);
    }
  }, [currentRosFeature.setting]);

  useEffect(() => {
    if (currentRosFeature.color) {
      setCurrentColors(currentRosFeature.color);
    }
  }, [currentRosFeature.color]);

  useEffect(() => {
    if (!currentRosFeature?.options) return;

    const normalize = (arr) =>
      sortBy(
        (arr || []).map(({ body }) => String(body).trim()),
        (item) => item.toLowerCase()
      );

    const optionsChanged = !isEqual(
      normalize(currentRosFeature.options),
      normalize(interactionResponseRef.current)
    );

    if (!optionsChanged) return;

    const uniqueOptions = currentRosFeature.options.filter((option, index, self) => {
      const key = String(option.body).trim().toLowerCase();
      return index === self.findIndex((t) => String(t.body).trim().toLowerCase() === key);
    });

    const mergedOptions = uniqueOptions.map((option) => {
      const existing = (interactionResponseRef.current || []).find(
        (o) => String(o.body).trim().toLowerCase() === String(option.body).trim().toLowerCase()
      );
      return existing ? { ...existing } : { ...option, contribution: 0 };
    });

    setInteractionResponse(mergedOptions);
  }, [currentRosFeature?.options]);

  useEffect(() => {
    interactionResponseRef.current = interactionResponse;
  }, [interactionResponse]);
  useEffect(() => {
    if (multipleChoiceId) {
      const logData = {
        streamId,
        interactionType: 'multipleChoice',
        interactionId: multipleChoiceId
      };

      setFirestoreUnsub({
        unSub: onSnapshot(doc(db, 'streams', streamId, 'multipleChoice', multipleChoiceId), (document) => {
          if (document.exists()) {
            let { data } = document.data();
            if (data) {
              setInteractionResponse(data);
              setShowNotification(false);
            }
            setTotalResponders(document.data().noOfResponders || 0);
          }
        }, (error) => logToCloudWatch('Failed to subscribe to firestore', { ...logData, error }, 'ERROR'))
      });
    }
    return () => {
      unsubscribeFromFirestore();
    };
  }, [multipleChoiceId]);
  const isShowAnswerVisible = showAnswerMap[currentRosFeature.id] || false;

  const relativeMax = Math.max(...interactionResponse?.map((option) => option.contribution ? parseInt(option.contribution) : 0));

  const showResponses = currentSettings?.showResponses !== false || !startInteraction;

  const [fontSize, setFontSize] = useState('text-4xl');
  const [roundedCorner, setRoundedCorner] = useState('rounded-[40px]');
  const [parentContainer, setParentContainer] = useState({ height: 'h-full', });
  const [optionNumContainer, setOptionNumContainer] = useState('w-[100px]');

  useEffect(() => {
    if (!interactionResponse) return;
    const {
      fontSize,
      roundedCorner,
      optionNumContainer,
      parentContainer,
    } = getBarLayout(interactionResponse.length);
    setFontSize(fontSize);
    setRoundedCorner(roundedCorner);
    setOptionNumContainer(optionNumContainer);
    setParentContainer(parentContainer);
  }, [interactionResponse]);

  useImperativeHandle(ref, () => ({
    async triggerEngagement({ interactionResult }) {
      if (interactionResult && interactionResult.length > 0) {
        await handleMultipleChoice({ interactionId: interactionResult[0].id, interactionResult: interactionResult[0].options });
      }
    }
  }));
  return (
    <>
      <div className={'w-full h-full justify-center overflow-hidden  flex flex-col  transition-transform ease-in-out duration-1000'}>
        {interactionResponse.map((option, index) => (
          <MultipleChoiceOptions
            key={option.key || option.id || `option-${index}`}
            showResponses={showResponses}
            colors={currentColors}
            option={option}
            settings={currentSettings}
            index={index}
            relativeMax={relativeMax}
            showAnswer={isShowAnswerVisible}
            totalOptionCount={interactionResponse?.length}
            fontSize={fontSize}
            roundedCorner={roundedCorner}
            optionNumContainer={optionNumContainer}
            parentContainer={parentContainer}
          />
        ))}
      </div>
    </>
  );
});

export default MultipleChoice;
